import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// Balloon (residual value) car loan maths.
//   i  = monthly rate = annualRate / 100 / 12
//   B  = balloon (residual) amount = loanAmount * balloonPct / 100, due as a
//        lump sum at the end of the term
//   a  = annuity factor = (1 - (1+i)^-n) / i
//   M  = monthly repayment = (P - B*(1+i)^-n) / a
// Total paid over the loan = M*n + B; total interest = total paid - P.
// Worked check: P=30000, 7% p.a., n=60, balloon 30% (B=9000) -> M≈$468.32,
// total interest ≈ $7,099 (vs ≈$5,642 with no balloon — lower repayment, more
// interest, because principal stays higher for longer).
const calcBalloon = (P: number, annualRate: number, n: number, B: number) => {
  const i = annualRate / 100 / 12;
  if (i === 0) {
    const monthly = (P - B) / n;
    return { monthly, totalInterest: monthly * n + B - P };
  }
  const discount = Math.pow(1 + i, -n);
  const annuity = (1 - discount) / i;
  const monthly = (P - B * discount) / annuity;
  const totalInterest = monthly * n + B - P;
  return { monthly, totalInterest };
};

const BalloonPaymentCalculator: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState('30000');
  const [interestRate, setInterestRate] = useState('7.00');
  const [loanTerm, setLoanTerm] = useState('60');
  const [balloonPct, setBalloonPct] = useState('30');

  const [monthly, setMonthly] = useState(0);
  const [balloonAmount, setBalloonAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [noBalloonMonthly, setNoBalloonMonthly] = useState(0);

  useEffect(() => {
    const P = parseFloat(loanAmount);
    const rate = parseFloat(interestRate);
    const n = parseInt(loanTerm, 10);
    const pct = parseFloat(balloonPct);

    if (isNaN(P) || P <= 0 || isNaN(rate) || rate < 0 || isNaN(n) || n <= 0 || isNaN(pct) || pct < 0 || pct >= 100) {
      setMonthly(0);
      return;
    }

    const B = P * (pct / 100);
    const withBalloon = calcBalloon(P, rate, n, B);
    const noBalloon = calcBalloon(P, rate, n, 0);

    setBalloonAmount(B);
    setMonthly(withBalloon.monthly);
    setTotalInterest(withBalloon.totalInterest);
    setNoBalloonMonthly(noBalloon.monthly);
  }, [loanAmount, interestRate, loanTerm, balloonPct]);

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const monthlySaving = noBalloonMonthly - monthly;

  return (
    <>
      <SEOHead
        title="Balloon Payment Calculator Australia | Car Finance with Residual Value"
        description="Calculate how a balloon payment affects your car loan repayments. Our Australian calculator shows monthly repayments, total interest and the residual lump sum for any loan term."
        keywords="balloon payment calculator australia, residual value car loan, car loan with balloon, australian car finance residual, reduce car repayments, lump sum car payment"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Balloon Payment Car Loan Calculator",
              "description": "Calculate monthly repayments and total interest on a car loan with a balloon (residual) payment in Australia.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": { "@type": "Country", "name": "Australia" },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Balloon Payment Car Loan Calculator"
        description="See how a balloon (residual) payment lowers your monthly car loan repayments — and what it costs you in extra interest — for any Australian loan amount, rate and term."
        icon={CalculatorIcon}
        result={monthly > 0 ? `Monthly repayment: ${formatCurrency(monthly)} + ${formatCurrency(balloonAmount)} balloon` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                A balloon payment (also called a residual) is a lump sum you defer to the end of your loan. It lowers your monthly
                repayments, but you still owe — and pay interest on — that amount until the term ends.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input label="Loan Amount" value={loanAmount} onChange={setLoanAmount} unit="$" placeholder="e.g. 30000" />
            <Input label="Interest Rate (p.a.)" value={interestRate} onChange={setInterestRate} unit="%" placeholder="e.g. 7.00" />
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              >
                {[12, 24, 36, 48, 60, 72, 84].map((m) => (
                  <option key={m} value={m}>{m} months ({m / 12} years)</option>
                ))}
              </select>
            </div>
            <Input label="Balloon / Residual (% of loan)" value={balloonPct} onChange={setBalloonPct} unit="%" placeholder="e.g. 30" />
          </div>

          {monthly > 0 && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Your Balloon Loan Summary</h2>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-blue-700">Monthly Repayment</div>
                    <div className="text-2xl font-bold text-blue-800">{formatCurrency(monthly)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Balloon Due at End</div>
                    <div className="text-2xl font-bold text-gray-800">{formatCurrency(balloonAmount)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Total Interest</div>
                    <div className="text-2xl font-bold text-gray-800">{formatCurrency(totalInterest)}</div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800">
                    A {balloonPct}% balloon lowers your monthly repayment by{' '}
                    <strong>{formatCurrency(monthlySaving)}</strong> (from {formatCurrency(noBalloonMonthly)} to{' '}
                    {formatCurrency(monthly)}), but you must repay or refinance the{' '}
                    <strong>{formatCurrency(balloonAmount)}</strong> balloon when the loan ends.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Educational content */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">How a balloon payment works</h2>
            <p className="text-gray-600">
              With a balloon (residual) car loan, part of the amount borrowed is set aside as a lump sum due at the end of the term.
              Because you only pay down the rest of the balance in your monthly repayments, those repayments are smaller — but the
              deferred amount keeps accruing interest, so a balloon loan usually costs more in total interest than an equivalent loan
              with no balloon. Use our{' '}
              <Link className="text-blue-600 hover:underline" to="/car-loan-calculator/">car loan repayment calculator</Link> to compare a
              standard loan, or the{' '}
              <Link className="text-blue-600 hover:underline" to="/early-payoff-calculator/">early payoff calculator</Link> to see how
              extra repayments change the picture. For a plain-English walkthrough of the trade-offs, read our guide to{' '}
              <Link className="text-blue-600 hover:underline" to="/blog/balloon-payments-explained/">
                balloon payments explained
              </Link>.
            </p>

            <h3 className="text-lg font-semibold text-gray-800">Your options when the balloon is due</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li><strong>Pay it out</strong> in cash to own the vehicle outright.</li>
              <li><strong>Refinance</strong> the balloon into a new loan and keep making repayments.</li>
              <li><strong>Trade in or sell</strong> the vehicle and use the proceeds to clear the residual.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">What is a typical balloon payment on a car loan in Australia?</h3>
                <p className="text-gray-600">
                  Balloon amounts commonly range from about 20% to 50% of the amount financed, and lenders often cap the residual
                  based on the loan term (a longer term usually means a lower maximum balloon).
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Does a balloon payment reduce the total cost of my loan?</h3>
                <p className="text-gray-600">
                  No. It lowers your monthly repayment but generally increases total interest, because the deferred balloon amount
                  accrues interest for the whole term.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Can I pay a balloon loan off early?</h3>
                <p className="text-gray-600">
                  Usually yes, though some lenders charge early termination or break fees. Check your contract, and estimate the
                  impact with our{' '}
                  <Link className="text-blue-600 hover:underline" to="/early-payoff-calculator/">early payoff calculator</Link>.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Are balloon payments common for business vehicles?</h3>
                <p className="text-gray-600">
                  Yes. Balloons are frequently used with commercial finance such as a chattel mortgage, where they help manage cash
                  flow. Speak to your accountant about any GST or tax treatment for business use.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Is a novated lease different from a balloon loan?</h3>
                <p className="text-gray-600">
                  Yes — a novated lease also has a residual, but it's a salary-packaging arrangement with different tax treatment.
                  See our guide to a{' '}
                  <Link className="text-blue-600 hover:underline" to="/blog/novated-lease-vs-traditional-car-loan/">
                    novated lease vs a traditional car loan
                  </Link>.
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">
              This calculator provides estimates for general information only and is not financial advice. Your lender's quoted
              repayments and residual may differ. Consider seeking advice for your circumstances.
            </p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default BalloonPaymentCalculator;
